import { useState } from "react";

import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { maskAddress } from "@hl/shared-features/lib/utils/content";
import { Anchor, Divider, Group, Stack, Text } from "@mantine/core";
import { ethers } from "ethers";

import {
  _CollectionType,
  TokenDistributionType,
  V1Royalty,
} from "apollo/graphql.generated";
import { EditPaymentRecipient } from "~/features/tokens/sections/ManageToken/modals/EditPaymentRecipient";
import { EditRoyalties } from "~/features/tokens/sections/ManageToken/modals/EditRoyalties";
import { PropertyBox } from "~/features/tokens/sections/ManageToken/properties/PropertyBox";
import { GetManageTokenCollectionQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const PayoutAndRoyaltyProperty = ({
  royalty,
  publicMintVector,
  paymentRecipient,
  distributionType,
  collectionId,
  editionId,
  address,
  collectionType,
  chainId,
  isCollectionReveal,
  refetchCollection,
  refetchAuction,
  enableEdit,
}: {
  royalty: V1Royalty | null;
  distributionType: TokenDistributionType;
  paymentRecipient?: string;
  publicMintVector?: GetManageTokenCollectionQuery["getCollection"]["mintVectors"][0];
  collectionId: string;
  editionId?: number | null;
  address: string;
  collectionType: _CollectionType;
  chainId: number;
  isCollectionReveal: boolean | null;
  refetchCollection?: any;
  refetchAuction?: any;
  enableEdit: boolean;
}) => {
  const [showRoyaltiesModal, setShowRoyaltiesModal] = useState<boolean>(false);
  const [showPaymentRecipientModal, setShowPaymentRecipientModal] =
    useState<boolean>(false);

  const royaltyInput: V1Royalty = {
    royaltyPercentageString: royalty?.royaltyPercentageString ?? "0",
    royaltyRecipient: royalty?.royaltyRecipient,
  };

  const isMint = distributionType === TokenDistributionType.Mint;

  return (
    <>
      <PropertyBox
        label="Payouts and royalties"
        value={
          <Stack w="100%" spacing={14}>
            {isMint && (
              <>
                <Group position="apart">
                  <Text size="sm">Primary sales payout address</Text>
                  <Group spacing={8}>
                    <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                      {paymentRecipient
                        ? maskAddress(paymentRecipient, 6, 4)
                        : "n/a"}
                      {enableEdit ? " · " : ""}
                    </Text>
                    {enableEdit && (
                      <Anchor
                        underline={false}
                        onClick={() => setShowPaymentRecipientModal(true)}
                      >
                        <Text size="sm" fw={500}>
                          Edit
                        </Text>
                      </Anchor>
                    )}
                  </Group>
                </Group>
                <Divider />
              </>
            )}
            <Group position="apart">
              <Text size="sm">Royalty amount</Text>
              <Group spacing={8}>
                <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                  {royaltyInput.royaltyPercentageString + "%"}
                  {enableEdit ? " ·" : ""}
                </Text>
                {enableEdit && (
                  <Anchor
                    underline={false}
                    onClick={() => setShowRoyaltiesModal(true)}
                  >
                    <Text size="sm" fw={500}>
                      Edit
                    </Text>
                  </Anchor>
                )}
              </Group>
            </Group>
            <Divider />
            <Group position="apart">
              <Text size="sm">Royalty payout address</Text>
              <Group spacing={8}>
                <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                  {royaltyInput.royaltyRecipient &&
                  royaltyInput.royaltyRecipient !== ethers.constants.AddressZero
                    ? maskAddress(royaltyInput.royaltyRecipient, 6, 4)
                    : "n/a"}
                  {enableEdit ? " · " : ""}
                </Text>
                {enableEdit && (
                  <Anchor
                    underline={false}
                    onClick={() => setShowRoyaltiesModal(true)}
                  >
                    <Text size="sm" fw={500}>
                      Edit
                    </Text>
                  </Anchor>
                )}
              </Group>
            </Group>
          </Stack>
        }
      />
      <EditRoyalties
        collectionId={collectionId}
        collectionType={collectionType}
        address={address}
        royalty={{
          ...royaltyInput,
          royaltyRecipient:
            royaltyInput.royaltyRecipient === ethers.constants.AddressZero
              ? null
              : royaltyInput.royaltyRecipient,
        }}
        editionId={editionId}
        chainId={chainId}
        isOpened={showRoyaltiesModal}
        refetchCollection={refetchCollection}
        refetchAuction={refetchAuction}
        onClose={() => setShowRoyaltiesModal(false)}
      />
      <EditPaymentRecipient
        collectionType={collectionType}
        distributionType={distributionType}
        collectionId={collectionId}
        paymentRecipient={paymentRecipient}
        publicMintVector={publicMintVector}
        isOpened={showPaymentRecipientModal}
        refetchCollection={refetchCollection}
        isCollectionReveal={isCollectionReveal}
        onClose={() => setShowPaymentRecipientModal(false)}
      />
    </>
  );
};
